import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import {
  bootAnonymous,
  identify,
  shutdownIntercom,
} from '../services/intercom';

/**
 * Hook to manage the Intercom messenger based on authentication state.
 *
 * Intercom is booted anonymously for every visitor so support chat is
 * available without logging in. Once a user authenticates, the session is
 * re-identified so the conversation is associated with their profile.
 *
 * Mounted once at the app root so the messenger persists across all routes
 * (dashboard, draw wizard, draw details, audit log) for both anonymous and
 * logged-in visitors (JUD-2266).
 */
export const useIntercom = (): void => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Track which user (if any) the messenger is currently identified as, so we
  // only re-identify on a genuine change and reset cleanly on logout.
  const identifiedUserIdRef = useRef<string | null>(null);
  const hasBootedRef = useRef(false);

  // Boot anonymously once, as early as possible, so the messenger is available
  // to visitors who never log in.
  useEffect(() => {
    if (hasBootedRef.current) {
      return;
    }
    hasBootedRef.current = true;
    bootAnonymous();
  }, []);

  useEffect(() => {
    // Wait for auth to resolve before reacting to the authenticated state.
    if (isLoading) {
      return;
    }

    if (isAuthenticated && user) {
      const userId = user.sub || user.email;
      if (!userId || identifiedUserIdRef.current === userId) {
        return;
      }
      identifiedUserIdRef.current = userId;

      void (async () => {
        let userJwt: string | undefined;
        try {
          const { intercom_user_jwt } = await apiService.getIntercomToken();
          userJwt = intercom_user_jwt ?? undefined;
        } catch {
        }
        identify({ userId, email: user.email, name: user.name, userJwt });
      })();
      return;
    }

    // User logged out (or session ended): end the identified session and start
    // a fresh anonymous one so the next visitor doesn't inherit the chat.
    if (identifiedUserIdRef.current) {
      identifiedUserIdRef.current = null;
      shutdownIntercom();
      bootAnonymous();
    }
  }, [user, isAuthenticated, isLoading]);
};
